/**
 * EmailGatingService - Handles email capture and HubSpot form submissions
 * for the Tabletop Exercise lead generation flow
 */

import { logger } from './LoggingService';

// HubSpot Portal and Form Configuration
// NOTE: Update FORM_GUID after creating the form in HubSpot
const HUBSPOT_PORTAL_ID = '20569952';
const HUBSPOT_FORM_GUID = 'ea85f9a9-1a5c-446e-a653-12d54df3cc88';

export interface EmailGateSubmission {
  email: string;
  role: 'tech' | 'exec' | null;
  industry: string | null;
}

export interface SubmissionResult {
  success: boolean;
  error?: string;
}

/**
 * Submit email capture data to HubSpot Forms API
 */
export async function submitEmailToHubSpot(
  submission: EmailGateSubmission
): Promise<SubmissionResult> {
  const { email, role, industry } = submission;

  // Validate email format
  if (!isValidEmail(email)) {
    return { success: false, error: 'Invalid email format' };
  }

  const endpoint = `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_GUID}`;

  const payload = {
    submittedAt: Date.now(),
    fields: [
      { name: 'email', value: email },
    ],
    context: {
      pageUri: window.location.href,
      pageName: 'Tabletop Exercise - Email Gate',
    },
  };

  try {
    logger.info('Submitting email to HubSpot', 'EmailGatingService', {
      email: email.substring(0, 3) + '***', // Log partial email for privacy
      role,
      industry,
    });

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      logger.info('Email submitted successfully to HubSpot', 'EmailGatingService');
      return { success: true };
    } else {
      const errorText = await response.text();
      logger.error('HubSpot submission failed', 'EmailGatingService', {
        status: response.status,
        error: errorText,
      });
      return { success: false, error: `Submission failed: ${response.status}` };
    }
  } catch (error) {
    logger.error(
      'Error submitting to HubSpot',
      'EmailGatingService',
      {},
      error as Error
    );
    return { success: false, error: 'Network error, please try again' };
  }
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Analytics event types for tabletop exercise tracking
 */
export type AnalyticsEventName =
  | 'email_gate_view'
  | 'email_gate_submit'
  | 'email_gate_submit_post_exercise'
  | 'email_gate_skip'
  | 'email_cta_click_post_exercise'
  | 'onboarding_complete'
  | 'scenario_selected'
  | 'exercise_start'
  | 'exercise_complete'
  | 'simulation_restart'
  | 'facilitated_cta_click';

/**
 * Track analytics events for email gating and exercise flow
 */
export function trackEmailGateEvent(
  eventName: AnalyticsEventName,
  data?: Record<string, unknown>
): void {
  logger.info(`Analytics event: ${eventName}`, 'EmailGatingService', data);

  // If you add an analytics service (e.g., Google Analytics, Mixpanel),
  // integrate tracking here
  if (typeof window !== 'undefined' && (window as unknown as { gtag?: Function }).gtag) {
    (window as unknown as { gtag: Function }).gtag('event', eventName, data);
  }
}
